import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { useTeams, useDeleteTeam, useUpdateTeam } from "./hooks/useTeams";
import { TeamTable } from "./components/TeamTable";
import { TeamFilters } from "./components/TeamFilters";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "@/components/layout/hooks/usePageTitle";
import { Team } from "@/services/teamService";
import { GetTeamsFilters } from "@/services/teamService";
import { StaffNavigation } from "./components/Navigtaion";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useStaffAvailabilityRange } from "@/hooks/useStaffSchedule";

type SortBy = NonNullable<GetTeamsFilters["sortBy"]>;

export const TeamsPage: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const hotelId = useCurrentHotelId();

  const [filters, setFilters] = useState<GetTeamsFilters>({
    sortBy: null,
    sortOrder: null,
  });

  const [sortBy, setSortBy] = useState<SortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const { data, isLoading } = useTeams({
    ...filters,
    sortBy,
    sortOrder,
  });
  const today = format(new Date(), "yyyy-MM-dd");
  const { data: todayAvailability, isLoading: isTodayAvailabilityLoading } =
    useStaffAvailabilityRange(hotelId ?? "", today, today);

  const onSiteStaffIds = useMemo(() => {
    const ids = new Set<string>();

    for (const availability of todayAvailability ?? []) {
      if (!availability.days[0]?.isAvailable) {
        continue;
      }

      ids.add(availability.staffId);
      if (availability.profileId) {
        ids.add(availability.profileId);
      }
    }

    return ids;
  }, [todayAvailability]);

  const deleteTeam = useDeleteTeam();
  const updateTeam = useUpdateTeam();

  const totalPages = data ? Math.ceil(data.total / (filters.perPage || 10)) : 0;

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this team?")) {
      deleteTeam.mutate(id);
    }
  };

  const handleStatusToggle = (team: Team) => {
    updateTeam.mutate({
      id: team.id,
      data: { isActive: !team.isActive },
    });
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, currentPage: page });
  };

  const handleSort = (field: string) => {
    const newField = field as SortBy;
    if (sortBy !== newField) {
      setSortBy(newField);
      setSortOrder("asc");
    } else {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") {
        setSortBy(null);
        setSortOrder(null);
      } else setSortOrder("asc");
    }
  };

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-6">
        <StaffNavigation activeTab="teams" />

        <div className="flex items-center justify-between">
          <TeamFilters filters={filters} onFilterChange={setFilters} />
          <Button
            variant={"outline"}
            onClick={() => navigate("/staff/teams/add")}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add team
          </Button>
        </div>

        <TeamTable
          data={data?.data || []}
          isLoading={isLoading || isTodayAvailabilityLoading}
          onDelete={handleDelete}
          onStatusToggle={handleStatusToggle}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          currentPage={filters.currentPage || 1}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onSiteStaffIds={onSiteStaffIds}
        />
      </div>
    </div>
  );
};
